import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  DecoupledEditor,
  InlineEditor,
  EventInfo,
} from "ckeditor5";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import shortid from "shortid";

import { exists } from "@examspark/common";
import {
  Button,
  ContentStyles,
  FormContext,
  Toolbar,
  useDebouncedCallback,
} from "@examspark/common-ui";
import { IconResetHtmlLine } from "@examspark/common-ui/dist/components/icons";

import { createConfiguration } from "../configuration";
import { useRichContentConfig } from "../contexts/RichContentConfigProvider";
import {
  RichContentOptions,
  RichContentOptionsProducer,
  useRichContentOptions,
} from "../contexts/RichContentOptionsProvider";
import { useSharedToolbar } from "../contexts/SharedToolbarProvider";
import Analytics from "../plugins/Analytics";
import { isPluginWithStyles } from "../plugins/PluginWithStyles";
import { Editor, EditorVariant, isChangeEventInfo } from "../types";
import applyFixes from "../util/fixes";

import EditorPlaceholder from "./EditorPlaceholder";
import EditorStyles, { EditorWarning } from "./EditorStyles";
import { RenderQueue } from "./RenderQueue";

export type RichContentEditorProps = {
  value?: string | null;
  onReady?: (editor: Editor) => void;
  onChange?: (value: string | null, editor: Editor) => void;
  className?: string;
  variant?: EditorVariant;
  disabled?: boolean;
  options?: RichContentOptions | RichContentOptionsProducer;
  placeholder?: string;
};

const RichContentEditor = ({
  value,
  onReady,
  onChange,
  className,
  disabled,
  variant = "classic",
  options,
  placeholder,
}: RichContentEditorProps) => {
  const sharedToolbar = useSharedToolbar();
  const [editor, setEditor] = useState<Editor>();
  const [isFocused, setIsFocused] = useState(false);
  const [hasUnsupportedContent, setHasUnsupportedContent] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const formContext = useContext(FormContext);
  const optionsContext = useRichContentOptions(options);
  const configContext = useRichContentConfig();

  const editorClass =
    variant === "inline"
      ? InlineEditor
      : variant === "seamless"
        ? DecoupledEditor
        : ClassicEditor;

  //
  // Regenerate config and id when config-driving values change
  // Changing id ensures editor will re-render if config or variant changes
  // (Mostly only happens in Storybook)
  //
  const { config, id, extraStyles } = useMemo(() => {
    const id = `${shortid()}-${variant}`;

    const config = createConfiguration(optionsContext, configContext);

    config.placeholder = placeholder;

    const extraStyles =
      config.plugins
        ?.map((plugin) => {
          if (!isPluginWithStyles(plugin)) return null;
          return plugin.buildEditorStyles(config);
        })
        .filter(exists) ?? [];

    return { config, id, extraStyles };
  }, [variant, optionsContext, configContext, placeholder]);

  const onChangeInternal = useCallback(
    (e: EventInfo, editor: Editor) => {
      setHasUnsupportedContent(editor.hasUnsupportedContent ?? false);

      if (isChangeEventInfo(e) && e.state?.isDataController) {
        //
        // Do not raise onchange events for data changed through the setData property
        // This keeps us from triggering changes when the incoming value prop changed
        //
        return;
      }

      onChange?.(editor.getData(), editor);
    },
    [onChange],
  );

  //
  // Debounce the onChange because calling getData can be expensive for large
  // complex content, and it causes slowdowns in typing responsiveness.
  //
  const onChangeDebounced = useDebouncedCallback(onChangeInternal, 150);

  useEffect(() => {
    if (!editor?.plugins.has(Analytics)) return;

    editor.plugins.get(Analytics).setDefaultProperties({
      variant,
    });
  }, [editor, variant]);

  return (
    <ContentStyles>
      <EditorStyles
        className={className}
        variant={variant}
        isFocused={isFocused}
        extraStyles={extraStyles}
      >
        <RenderQueue placeholder={<EditorPlaceholder variant={variant} />}>
          <CKEditor
            id={id}
            editor={editorClass}
            config={config}
            //
            // Although CKEditor accepts undefined as its data, there are some plugins
            // (e.g. MathType) that don't handle this correctly
            //
            data={value ?? ""}
            disabled={disabled ?? formContext.disabled}
            onChange={onChangeDebounced}
            onReady={(editor: Editor) => {
              if (editor instanceof DecoupledEditor) {
                sharedToolbar.registerEditor(editor);
              }

              applyFixes(editor);

              if (statusRef.current != null) {
                editor.ui.focusTracker.add(statusRef.current);
              }

              editor.ui.focusTracker.on("change:isFocused", () => {
                setIsFocused(editor.ui.focusTracker.isFocused);
              });

              setHasUnsupportedContent(editor.hasUnsupportedContent ?? false);
              setEditor(editor);

              onReady?.(editor);
            }}
          />
        </RenderQueue>
        <div ref={statusRef}>
          {hasUnsupportedContent && (
            <EditorWarning>
              <Toolbar>
                The editor contains legacy HTML content.
                <Toolbar.Right>
                  <Button
                    title="Remove Legacy Content"
                    onClick={() => {
                      editor?.execute("scrubUnsupportedContent");
                      editor?.focus();
                    }}
                    size="sm"
                  >
                    <IconResetHtmlLine />
                    Remove
                  </Button>
                </Toolbar.Right>
              </Toolbar>
            </EditorWarning>
          )}
        </div>
      </EditorStyles>
    </ContentStyles>
  );
};

export default RichContentEditor;
